import { Form } from '@abbott-components/core';
import PageState from '../../Shared/pageState';
import Stack from '@mui/material/Stack';
import getPagePermissionForRole from '../../Shared/jsonSchemaUtilities/getPagePermissionForRole';
import hasRoleAccessToPage from '../../Shared/jsonSchemaUtilities/hasRoleAccessToPage';
import {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  WorkflowEngineActionKind,
  WorkflowEngineContext,
  WorkflowEngineMode
} from '../../WorkflowEngine/workflowEngineReducer';
import { useSnackbar } from 'notistack';
import getPageEntryYupSchema from './getPageEntryYupSchema';
import getPageDefaultValuesFromSchema from './getPageControlsDefaultValues';
import { get, post } from '../../Shared/ajax';
import { getErrorMessage } from '../../Shared/errorHandling';
import controlDictionary from '../../Controls/controlDictionary';
import { JSONSchema7 } from 'json-schema';
import { PagePermissionLevel } from '../../Shared/Enums/pagePermissionLevel';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import config from '../../Shared/config';
import {
  GetPageEntryResponsePage,
  GetPageEntryResponse,
  PostPageEntryResponse,
  PostPageEntryRequest,
  CompleteDynamicWorkflowSynchronousResponse,
  CompleteDynamicWorkflowSynchronousRequest,
  CompleteDynamicWorkflowRequest,
  CompleteDynamicWorkflowResponse
} from './types';
import ConditionalControls, {
  ConditionalControlsRef
} from './ConditionalControls';
import * as yup from 'yup';
import ControlKind from '../../Shared/controlKind';
import getControlSchemaProperty from '../../Shared/jsonSchemaUtilities/getControlSchemaProperty';
import generateCalculatedValue from '../../Controls/CalculatedValue/generateCalculatedValue';
import getWorkflowFlattenData from '../../Shared/getWorkflowFlattenData';
import FormPlaceHolder from './FormPlaceHolder';

export interface FormEntryRef {
  submit: (index: number) => Promise<void>;
}

export default forwardRef((page: PageState, ref) => {
  const { state, dispatch } = useContext(WorkflowEngineContext);

  const {
    pages,
    currentPage,
    role,
    mode,
    workflowData,
    workflowEntryExternalId,
    isReadOnlyView
  } = state;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSideNav, setIsSideNav] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();
  /**
   * Gets the **index** of the previous page for the current role
   * @returns number | undefined
   */
  const prevPageIndex = () => {
    for (let i = currentPage - 1; i < pages.length && i >= 0; i--) {
      if (hasRoleAccessToPage(mode, role, pages[i])) {
        return i;
      }
    }
    return undefined;
  };
  /**
   * Gets the previous available **page** for the current role
   * @returns page | undefined
   */
  const prevPage = () => {
    const prevValidIndex = prevPageIndex();
    if (prevValidIndex !== undefined) {
      return pages[prevValidIndex];
    } else {
      return undefined;
    }
  };
  /**
   * Gets the **index** of the next available page for the current role
   * @returns number | undefined
   */
  const nextPageIndex = () => {
    for (let i = currentPage + 1; i < pages.length; i++) {
      if (hasRoleAccessToPage(mode, role, pages[i])) {
        return i;
      }
    }
    return undefined;
  };
  /**
   * Gets the next available **page** for the current role
   * @returns page | undefined
   */
  const nextPage = () => {
    const nextValidIndex = nextPageIndex();
    if (nextValidIndex !== undefined) {
      return pages[nextValidIndex];
    } else {
      return undefined;
    }
  };

  const submitBtnRef = useRef<HTMLButtonElement>(null);
  const triggerValidationReff = useRef<ConditionalControlsRef>(null);

  const validateForm = async () => {
    let isValid = false;
    let updatedData: Record<string, unknown> | undefined;
    if (triggerValidationReff.current) {
      isValid = await triggerValidationReff.current.triggerValidation();
      updatedData = triggerValidationReff.current.sanitizeNumericFields();
    }
    dispatch({
      type: WorkflowEngineActionKind.UPDATE_PAGE_VALID,
      payload: { id: pages[currentPage].id, valid: isValid }
    });
    return { isValid, updatedData };
  };

  const handleStepChange = async (index: number) => {
    if (submitBtnRef.current) {
      const { isValid, updatedData } = await validateForm();
      if (isValid) {
        submitBtnRef.current.click();
      }

      let pageData: Record<string, unknown> = {};
      pageData =
        updatedData !== undefined ? updatedData : workflowData[currentPage];
      pageData = resetHiddenControlsValues(pageData);
      const complete = isPageComplete(pageData);
      if (complete !== undefined) {
        pageData[`${pages[currentPage].id}-pageValid`] = complete;
      }
      workflowData[currentPage] = pageData;
      updateWorkflowDataInState(workflowData);

      await submitPageEntryAsync(workflowEntryExternalId, page.id, pageData);

      dispatch({
        type: WorkflowEngineActionKind.SET_CURRENT_PAGE,
        payload: index
      });
    }
  };

  useImperativeHandle(ref, () => ({
    async submit(index: number) {
      setIsSideNav(true);
      handleStepChange(index);
    }
  }));

  /**
   * Validates if all pages are valid for edit access only
   * @returns bool
   */
  const isWorkFlowValid = () => {
    const availablePageList: PageState[] = [];
    for (let i = 0; i < pages.length; i++) {
      const pageSchema = (pages[i].schema.properties as JSONSchema7Properties)[
        'page'
      ] as JSONSchema7;
      const pageProperties = pageSchema.properties as JSONSchema7Properties;
      Object.getOwnPropertyNames(pageProperties).forEach((propName: string) => {
        if (
          pageProperties[propName].title === role &&
          pageProperties[propName].default === PagePermissionLevel.Edit
        ) {
          availablePageList.push(pages[i]);
          return;
        }
      });
    }
    const filteredPageValidState = state.pagesValidState.filter((validState) =>
      availablePageList.some((page) => page.id === validState.id)
    );
    for (const vldState of filteredPageValidState) {
      if (!vldState.valid && vldState.id !== pages[currentPage].id) {
        return false;
      }
    }
    return true;
  };

  const switchToPrevPage = async () => {
    const prevValidPage = prevPageIndex();
    if (prevValidPage === undefined) return;
    dispatch({
      type: WorkflowEngineActionKind.SET_CURRENT_PAGE,
      payload: prevValidPage
    });
  };

  const switchToNextPage = () => {
    const nextValidPage = nextPageIndex();
    if (nextValidPage === undefined || isSideNav) return;

    dispatch({
      type: WorkflowEngineActionKind.SET_CURRENT_PAGE,
      payload: nextValidPage
    });
  };

  const updateWorkflowDataInState = (data: Record<string, unknown>[]) => {
    dispatch({
      type: WorkflowEngineActionKind.SET_WORKFLOW_DATA,
      payload: data
    });
  };

  const setFormPermissions = () => {
    if (
      mode === WorkflowEngineMode.ENTRY ||
      mode === WorkflowEngineMode.PREVIEW
    ) {
      const pageSchema = (page.schema.properties as JSONSchema7Properties)[
        'page'
      ] as JSONSchema7;

      const userPermission = getPagePermissionForRole(pageSchema, role);

      dispatch({
        type: WorkflowEngineActionKind.SET_READ_ONLY_VIEW,
        payload: userPermission === PagePermissionLevel.ReadOnly
      });
    }
  };

  const mapResponseToEntryRecord = (
    pageEntry: GetPageEntryResponsePage,
    index: number
  ) => {
    const defaultPageData = getPageDefaultValuesFromSchema(pages[index]);
    const pageData = pageEntry.entryData ? JSON.parse(pageEntry.entryData) : {};
    const hasPageData = Object.getOwnPropertyNames(pageData).length > 0;
    return hasPageData ? JSON.parse(pageEntry.entryData) : defaultPageData;
  };

  const evaluateCalculatedValueForConditionalVisibility = (
    pages: PageState[],
    workflowData: Record<string, unknown>[]
  ): Record<string, unknown>[] => {
    const flattenWorkflowData = getWorkflowFlattenData(workflowData);

    for (const page of pages) {
      for (const control of page.controls) {
        if (control.kind !== ControlKind.CALCULATED_VALUE) continue;

        const expression = getControlSchemaProperty(
          control,
          'expression'
        ) as string;

        try {
          const calculatedValue = generateCalculatedValue(
            expression,
            pages,
            flattenWorkflowData
          );

          for (const data of workflowData) {
            if (Object.keys(data).includes(control.id)) {
              data[control.id] = isNaN(calculatedValue)
                ? null
                : calculatedValue;
              break;
            }
          }
        } catch {
          // ignore any calculations error happening here
        }
      }
    }

    return [...workflowData];
  };

  const loadFormInitialData = () => {
    let data: Record<string, unknown>[] = pages.map(
      (page: PageState, index: number) => {
        const defaultPageData = getPageDefaultValuesFromSchema(page);
        const currentPageData = workflowData[index];
        const pageData = currentPageData || defaultPageData;
        return pageData;
      }
    );

    // while loading the initial form data, evaluate calculated values to properly set it
    // in the workflow data for conditional visibility consideration
    data = evaluateCalculatedValueForConditionalVisibility(pages, data);

    updateWorkflowDataInState(data);
  };

  const retrieveEntryDataAsync = async (entryExternalId: string) => {
    setIsLoading(true);
    try {
      const response = await get<GetPageEntryResponse>(
        `DynamicWorkflowPageEntry?entryExternalId=${entryExternalId}`
      );

      const data: Record<string, unknown>[] = response.pageEntries.map(
        mapResponseToEntryRecord
      );

      updateWorkflowDataInState(data);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
    setIsLoading(false);
  };
  const displayCompletionError = () => {
    enqueueSnackbar('Please complete all pages before submitting the form', {
      variant: 'error',
      preventDuplicate: true
    });
  };

  const completeEntryAsync = async (
    entryExternalId: string,
    pageExternalId: string,
    formData: Record<string, unknown>
  ) => {
    setIsLoading(true);
    try {
      const response = await post<
        CompleteDynamicWorkflowResponse,
        CompleteDynamicWorkflowRequest
      >(`DynamicWorkflowCompletion`, {
        entryExternalId,
        pageExternalId,
        formData: JSON.stringify(formData)
      });
      await completeEntrySynchronous(
        entryExternalId,
        response.completionState,
        response.formOutcomeStatus
      );
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
      return false;
    } finally {
      setIsLoading(false);
    }
    return true;
  };

  const completeEntrySynchronous = async (
    entryExternalId: string,
    completionState: string,
    formOutcomeStatus: string
  ) => {
    if (!config.workflowCompletionSynchronousUrl) return;
    try {
      await post<
        CompleteDynamicWorkflowSynchronousResponse,
        CompleteDynamicWorkflowSynchronousRequest
      >(config.workflowCompletionSynchronousUrl, {
        entryExternalId: entryExternalId,
        completionState: completionState,
        formOutcomeStatus: formOutcomeStatus,
        entryRole: config.role
      });
    } catch (err) {
      if (config.workflowCompletionSynchronousErrorMessage) {
        enqueueSnackbar(config.workflowCompletionSynchronousErrorMessage, {
          variant: 'error'
        });
      } else {
        enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
      }
    }
  };

  const resetHiddenControlsValues = (
    formData: Record<string, unknown>
  ): Record<string, unknown> => {
    const startsWtihGUIDRegex =
      /^control-[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
    const updatedFormData = { ...formData };

    for (const key of Object.keys(updatedFormData)) {
      const matches = startsWtihGUIDRegex.exec(key);
      if (!matches?.length) continue;
      const control = page.controls.find((c) => c.id === matches[0]);

      if (control && !control.visible) {
        updatedFormData[key] =
          controlDictionary[control.kind].getDefaultValue(control)[key];
      }
    }

    return updatedFormData;
  };

  const submitPageEntryAsync = async (
    entryExternalId: string | undefined,
    pageExternalId: string,
    formData: Record<string, unknown>
  ) => {
    if (!entryExternalId) {
      switchToNextPage();
      return;
    }

    try {
      setIsLoading(true);
      await post<PostPageEntryResponse, PostPageEntryRequest>(
        `DynamicWorkflowPageEntry`,
        {
          entryExternalId,
          pageExternalId,
          formData: JSON.stringify(formData),
          pageValid: formData[`${pages[currentPage].id}-pageValid`] as boolean
        }
      );
      switchToNextPage();
    } catch (err) {
      setIsLoading(false);
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
  };

  const displayCompletionScreen = () => {
    dispatch({
      type: WorkflowEngineActionKind.SET_WORKFLOW_COMPLETED,
      payload: true
    });
  };

  const isPageComplete = (pageData: Record<string, unknown>) => {
    const pageSchema = (
      pages[currentPage].schema.properties as JSONSchema7Properties
    )['page'] as JSONSchema7;
    const userPermission = getPagePermissionForRole(pageSchema, role);

    const requiredControls = pages[currentPage].controls.filter((control) => {
      const requiredProperty = control.schema?.properties
        ?.required as JSONSchema7Properties;
      return control.visible && requiredProperty && requiredProperty.default;
    });
    const allRequiredControlsHaveValues = requiredControls.every((control) => {
      const controlId = control?.id;
      const controlKind = control?.kind;
      const controlHasData =
        (controlKind === 'numeric' && pageData[controlId] === 0) ||
        pageData[controlId] ||
        pageData[controlId + '-value'];
      return controlHasData;
    });

    return userPermission != PagePermissionLevel.Edit
      ? undefined
      : allRequiredControlsHaveValues;
  };

  const handleSubmit = async (data: Record<string, unknown>) => {
    if (!isSideNav) {
      const pageData = resetHiddenControlsValues(data);
      const complete = isPageComplete(pageData);
      if (complete !== undefined) {
        pageData[`${pages[currentPage].id}-pageValid`] = complete;
      }

      workflowData[currentPage] = pageData;
      updateWorkflowDataInState(workflowData);

      dispatch({
        type: WorkflowEngineActionKind.UPDATE_PAGE_VALID,
        payload: { id: pages[currentPage].id, valid: true }
      });

      if (nextPage() === undefined) {
        if (isWorkFlowValid()) {
          if (workflowEntryExternalId) {
            const isSuccessful = await completeEntryAsync(
              workflowEntryExternalId,
              page.id,
              pageData
            );
            if (!isSuccessful) {
              return;
            }
          }

          displayCompletionScreen();

          return;
        } else {
          displayCompletionError();
          return;
        }
      } else {
        await submitPageEntryAsync(workflowEntryExternalId, page.id, pageData);
      }
    }
  };

  const renderForm = () => {
    if (!workflowData[currentPage]) return null;

    const pageSchema = (page.schema.properties as JSONSchema7Properties)[
      'page'
    ] as JSONSchema7;
    const userPermission = getPagePermissionForRole(pageSchema, role);
    const pageReadOnly = userPermission === PagePermissionLevel.ReadOnly;

    return (
      <Form
        validationSchema={yup.object(getPageEntryYupSchema(page))}
        onFormSubmit={handleSubmit}
        formOptions={{
          mode: 'onChange',
          defaultValues: workflowData[currentPage]
        }}
      >
        <Card variant="outlined">
          <CardContent sx={{ padding: 5 }}>
            <ConditionalControls
              pageControls={page.controls}
              ref={triggerValidationReff}
            />
          </CardContent>
        </Card>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ marginTop: 2, marginLeft: 1 }}
          spacing={1}
        >
          {prevPage() !== undefined && (
            <Button
              type="button"
              variant="contained"
              color="secondary"
              startIcon={<ChevronLeftIcon />}
              onClick={switchToPrevPage}
            >
              {'Back'}
            </Button>
          )}
          <Button
            type={isReadOnlyView ? 'button' : 'submit'}
            variant="contained"
            color="primary"
            endIcon={<ChevronRightIcon />}
            ref={submitBtnRef}
            onClick={
              pageReadOnly
                ? () => {
                    nextPage() === undefined
                      ? handleSubmit({})
                      : switchToNextPage();
                  }
                : undefined
            }
          >
            {nextPage() === undefined ? 'Submit' : 'Next'}
          </Button>
        </Stack>
      </Form>
    );
  };

  useEffect(() => {
    if (workflowEntryExternalId && workflowData.length === 0) {
      retrieveEntryDataAsync(workflowEntryExternalId);
    } else {
      loadFormInitialData();
    }
  }, []);

  useEffect(() => {
    setFormPermissions();
  }, [currentPage]);

  useEffect(() => {
    document
      .querySelector('.MuiStepper-horizontal>.Mui-active')
      ?.scrollIntoView({
        inline: 'center'
      });

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  return isLoading ? <FormPlaceHolder {...page} /> : renderForm();
});
